import "./ApolloGradeChart.css";
import {
  ApolloGradeScopeLabels,
  ApolloGradeValueLabels,
  ApolloGradeValueShortLabels,
} from "@/pages/reports/reports-const";
import { colors } from "@/shared/utils/colors";
import { ApolloGrade, ApolloGradeScope } from "@suns/api";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import {
  PlayerMetadataApolloGradeRow,
  PlayerMetadataRow,
} from "@suns/api/generated-client/apollo";
import { Card, Flex, Grid, Text, cn } from "@suns/design-system";
import {
  ApolloAnalyticsIcon,
  ApolloBadgeIcon,
  ApolloScoutIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@suns/design-system/icons";
import { Fragment } from "react";

export function computeChartPositions(
  lowGrade: PlayerMetadataApolloGradeRow,
  middleGrade: PlayerMetadataApolloGradeRow | null,
  highGrade: PlayerMetadataApolloGradeRow
) {
  let min = Math.min(lowGrade.numericalValue, highGrade.numericalValue);
  let max = Math.max(lowGrade.numericalValue, highGrade.numericalValue);

  if (lowGrade.value === highGrade.value) {
    max = Math.ceil(max);
    min = Math.floor(min);
  }

  const left = min <= 1 ? 0 : ((min - 1) / 8) * 100;
  const right = max > 8 ? 100 : ((max - 1) / 8) * 100;
  const middle = middleGrade
    ? ((middleGrade.numericalValue - min) / (max - min)) * 100
    : null;

  return { left, middle, right };
}

export default function ApolloGradeChart({
  metadata,
  displayNumbers = false,
  print = false,
}: {
  metadata: PlayerMetadataRow;
  displayNumbers?: boolean;
  grouping?: PlayerMetadataApolloGradeRow.grouping;
  print?: boolean;
}) {
  const hybridGrades = [
    metadata?.hybridGradeCurrent,
    metadata?.hybridGradeRemainingCapacity,
    metadata?.hybridGradeCeiling,
    metadata?.hybridGradeBullseye,
    metadata?.hybridGradeBasement,
  ]
    .filter(Boolean)
    .sort((a, b) => a!.numericalValue - b!.numericalValue);

  const scoutGrades = [
    metadata?.scoutGradeCurrent,
    metadata?.scoutGradeRemainingCapacity,
    metadata?.scoutGradeCeiling,
    metadata?.scoutGradeBullseye,
    metadata?.scoutGradeBasement,
  ].filter(Boolean);

  const analyticGrades = [
    metadata?.analyticGradeCurrent,
    metadata?.analyticGradeRemainingCapacity,
    metadata?.analyticGradeCeiling,
    metadata?.analyticGradeBullseye,
    metadata?.analyticGradeBasement,
  ].filter(Boolean);

  if (hybridGrades.length < 2) {
    return null;
  }

  const lowGrade = hybridGrades[0]!;
  const scoutLowGrade = scoutGrades.find(
    (grade) => grade?.scope === lowGrade.scope
  );
  const analyticLowGrade = analyticGrades.find(
    (grade) => grade?.scope === lowGrade.scope
  );

  const middleGrade = hybridGrades.length === 3 ? hybridGrades[1] : null;
  const scoutMiddleGrade = middleGrade
    ? scoutGrades.find((grade) => grade?.scope === middleGrade.scope)
    : null;
  const analyticMiddleGrade = middleGrade
    ? analyticGrades.find((grade) => grade?.scope === middleGrade.scope)
    : null;

  const highGrade = hybridGrades[hybridGrades.length - 1]!;
  const scoutHighGrade = scoutGrades.find(
    (grade) => grade?.scope === highGrade.scope
  );
  const analyticHighGrade = analyticGrades.find(
    (grade) => grade?.scope === highGrade.scope
  );

  const { left, middle, right } = computeChartPositions(
    lowGrade,
    middleGrade ?? null,
    highGrade
  );

  const leftCard = (
    <LegendCard
      title={ApolloGradeScopeLabels[lowGrade.scope as ApolloGradeScope]}
      hybridGradeValue={lowGrade.value}
      analyticNumericalValue={analyticLowGrade?.numericalValue}
      scoutNumericalValue={scoutLowGrade?.numericalValue}
      print={print}
      hasMiddleCard={hybridGrades.length > 2}
    />
  );

  const middleCard = middleGrade ? (
    <LegendCard
      title={ApolloGradeScopeLabels[middleGrade.scope as ApolloGradeScope]}
      hybridGradeValue={middleGrade.value}
      analyticNumericalValue={analyticMiddleGrade?.numericalValue}
      scoutNumericalValue={scoutMiddleGrade?.numericalValue}
      print={print}
      hasMiddleCard={hybridGrades.length > 2}
    />
  ) : null;

  const rightCard = (
    <LegendCard
      title={ApolloGradeScopeLabels[highGrade.scope as ApolloGradeScope]}
      hybridGradeValue={highGrade.value}
      analyticNumericalValue={analyticHighGrade?.numericalValue}
      scoutNumericalValue={scoutHighGrade?.numericalValue}
      print={print}
      hasMiddleCard={hybridGrades.length > 2}
    />
  );

  const cardWidth = print ? 140 : 200;

  const isImproving =
    lowGrade.scope === PlayerMetadataApolloGradeRow.scope.CURRENT;

  const barWidthPercent = right - left;
  const requiredLegendWidth = `calc(${barWidthPercent}% + ${cardWidth}px + ${hybridGrades.length * 10}px)`;
  const minLegendWidth =
    cardWidth * hybridGrades.length + (hybridGrades.length - 1) * 10;
  const legendWidth = `max(${requiredLegendWidth}, ${minLegendWidth}px)`;

  return (
    <Flex direction="down" gap="sm">
      <Flex direction="right" className="w-full">
        <Flex
          direction="right"
          justify="between"
          gap="sm"
          className="apollo-grade-chart-container"
          style={
            {
              "--legend-width": legendWidth,
              "--min-legend-width": `${minLegendWidth}px`,
              "--margin-left": `max(0px, min(calc(${left}% + ${barWidthPercent}% / 2 - (${legendWidth} / 2)), calc(100% - ${minLegendWidth}px)))`,
            } as React.CSSProperties
          }
        >
          {leftCard}
          {middleCard && middle != null ? (
            <Flex
              style={{
                flex: 1,
                position: "relative",
                marginLeft: `-${cardWidth / 2}px`,
                marginRight: `-${cardWidth / 2}px`,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: `max(${cardWidth / 2}px, min(calc(${middle}% - ${cardWidth / 2}px), calc(100% - ${cardWidth * 1.5}px)))`,
                }}
              >
                {middleCard}
              </div>
            </Flex>
          ) : null}
          {rightCard}
        </Flex>
      </Flex>

      <Flex direction="down" gap="none">
        <div
          className={cn(
            "relative w-full rounded-md bg-gray-300",
            print ? "h-6" : "h-8"
          )}
        >
          <Flex
            direction="right"
            justify="evenly"
            className="absolute left-[1px] right-0 h-full"
          >
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-full w-[1px] bg-gray-400" />
            ))}
          </Flex>
          <div
            className="absolute left-0 right-0 h-full rounded-md bg-gradient-to-r from-secondary-600
              via-orange-500 to-yellow-500"
            style={{
              clipPath: `inset(0 ${100 - right}% 0 ${left}% round 6px)`,
            }}
          ></div>
          <div
            className="absolute flex h-full items-center justify-center opacity-75"
            style={{
              left: `${left}%`,
              width: `${(right - left).toFixed(2)}%`,
            }}
          >
            {hybridGrades.length === 2 &&
              highGrade.value !== lowGrade.value &&
              (isImproving ? (
                <>
                  <div className="-mr-2 ml-2 h-[2px] max-w-[30%] flex-grow bg-white"></div>
                  <ArrowRightIcon size={26} color="white" />
                </>
              ) : (
                <>
                  <ArrowLeftIcon size={26} color="white" />
                  <div className="-ml-2 mr-2 h-[2px] max-w-[40%] flex-grow bg-white"></div>
                </>
              ))}
            {hybridGrades.length === 3 && (
              <div
                className="h-full bg-white"
                style={{
                  position: "absolute",
                  left: `${middle?.toFixed(0)}%`,
                  width: "2px",
                }}
              />
            )}
          </div>
        </div>
        <Grid className="grid-cols-8 gap-0 divide-x text-center">
          {Object.keys(ApolloGrade)
            .reverse()
            .map((grade, i) => {
              const active =
                grade === metadata.hybridGradeCurrent?.value ||
                grade === metadata.hybridGradeRemainingCapacity?.value;
              return (
                <Fragment key={`grade-${grade}-${i}`}>
                  <Text
                    size={print ? "3xs" : "sm"}
                    heading={active}
                    muted={!active}
                    className="col-span-1 hidden pt-1 md:block"
                  >
                    {
                      ApolloGradeValueLabels[
                        grade as keyof typeof ApolloGradeValueLabels
                      ]
                    }
                  </Text>
                  <Text
                    size="sm"
                    heading={active}
                    muted={!active}
                    className="col-span-1 pt-1 md:hidden"
                  >
                    {
                      ApolloGradeValueShortLabels[
                        grade as keyof typeof ApolloGradeValueShortLabels
                      ]
                    }
                  </Text>
                </Fragment>
              );
            })}
        </Grid>
        {displayNumbers && (
          <Flex direction="right" justify="between">
            {[...Array(9)].map((_, i, indices) =>
              i < indices.length - 1 ? (
                <Text size="3xs">{i + 1}</Text>
              ) : (
                <Flex className="w-1" />
              )
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

function LegendCard({
  title,
  hybridGradeValue,
  analyticNumericalValue,
  scoutNumericalValue,
  print = false,
  hasMiddleCard = false,
}: {
  title: string;
  hybridGradeValue: ApolloGrade;
  analyticNumericalValue?: number;
  scoutNumericalValue?: number;
  print?: boolean;
  hasMiddleCard?: boolean;
}) {
  const textSize = print ? "3xs" : "xs";
  return (
    <Card
      className={cn(
        "print:rounded-lg print:border-solid print:border-gray-300",
        hasMiddleCard && "max-sm:w-[120px]",
        print
          ? "h-11 w-[140px] px-2 py-[5px]"
          : "h-14 w-[200px] p-2 max-sm:h-16"
      )}
    >
      <Flex direction="right" gap="xs" justify="between" className="h-full">
        <Flex direction="down" gap="none" justify="between">
          <Text size={textSize} muted>
            {title === "Grade" ? "Current" : title}
          </Text>
          <Text size={textSize} heading>
            {ApolloGradeValueLabels[hybridGradeValue]}
          </Text>
          <Flex
            direction="right"
            gap="sm"
            className={cn(print ? "hidden" : "md:hidden")}
          >
            {analyticNumericalValue && (
              <Flex direction="right" gap="xs" align="center">
                <ApolloBadgeIcon size={12} color={colors.bugambilia} />
                <Text size={textSize} heading>
                  {analyticNumericalValue.toFixed(1)}
                </Text>
              </Flex>
            )}
            {scoutNumericalValue && (
              <Flex direction="right" gap="xs" align="center">
                <ApolloBadgeIcon size={12} color={colors.xihuitl} />
                <Text size={textSize} heading>
                  {scoutNumericalValue.toFixed(1)}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>

        <Flex
          direction="right"
          gap="sm"
          className={cn("hidden md:flex print:flex", hasMiddleCard && "hidden")}
        >
          {analyticNumericalValue && (
            <Flex direction="down" gap="none" justify="between">
              <ApolloAnalyticsIcon
                size={print ? 16 : 22}
                color={colors.bugambilia}
              />
              <Text size={textSize} heading>
                {analyticNumericalValue.toFixed(1)}
              </Text>
            </Flex>
          )}
          {scoutNumericalValue && (
            <Flex direction="down" gap="none" justify="between">
              <ApolloScoutIcon size={print ? 16 : 22} color={colors.xihuitl} />
              <Text size={textSize} heading>
                {scoutNumericalValue.toFixed(1)}
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Card>
  );
}
